import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Chip, IconButton, Tooltip, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from 'elements/Box';
import EmptyElement from 'elements/EmptyElement';
import { ApiRequest } from 'GlobalFunctions';
import { useSnackbar } from 'notistack';

const PROCEDIMIENTO_LABELS: Record<string, string> = {
  curacion_simple: 'Curación simple',
  curacion_avanzada: 'Curación avanzada de heridas',
  ostomia: 'Ostomía',
  sondas: 'Sondas',
  retiro_puntos: 'Retiro de puntos',
  inyecciones: 'Inyecciones',
  tratamiento_endovenoso: 'Tratamiento endovenoso / Sueroterapia',
  asesoria_online: 'Asesoría online',
  otro: 'Otro procedimiento',
};

export default function QuoteRequestsList(props: any) {
  const { setLoading } = props;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [list, setList] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    setLoading(true);
    ApiRequest({
      url: '/quotes/admin-list',
      method: 'GET',
      setResponse: (res: any) => {
        if (res.status === 'error') {
          setErrorMsg(res.message || 'No se pudo cargar el listado');
        } else {
          setList(res.list || []);
        }
        setLoading(false);
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = (request_id: string) => {
    const link = `${window.location.origin}/admin/cotizaciones/${request_id}`;
    navigator.clipboard.writeText(link);
    enqueueSnackbar('Link copiado.', { variant: 'success' });
  };

  const filtered = list.filter(r => {
    const q = search.toLowerCase();
    return (
      (r.nombre || '').toLowerCase().includes(q) ||
      (r.apellido || '').toLowerCase().includes(q) ||
      (r.telefono || '').toLowerCase().includes(q) ||
      (r.comuna_name || '').toLowerCase().includes(q)
    );
  });

  const thSx = {
    color: '#94a3b8', fontWeight: 700, fontSize: 11,
    textTransform: 'uppercase' as const,
    borderBottom: '1px solid #f1f5f9', py: 1.5,
  };

  return (
    <div className="content">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Solicitudes de cotización</h2>
            <span className="h2_subtitle">{list.length} solicitudes recibidas</span>
          </div>
        </div>

        <Box id="QuoteRequestsList" className="box100" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px 4px' }}>
            <TextField
              size="small"
              placeholder="Buscar por paciente, teléfono o comuna..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 360, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
            />
          </div>

          {errorMsg && (
            <div style={{ padding: '0 16px 12px' }}>
              <Alert severity="error">{errorMsg}</Alert>
            </div>
          )}

          <div style={{ padding: '8px 0 16px' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '40px 0' }}>
                <EmptyElement title="Sin solicitudes" subtitle="Aún no hay solicitudes de pacientes registradas" />
              </div>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thSx, pl: 3 }}>Fecha</TableCell>
                      <TableCell sx={thSx}>Paciente</TableCell>
                      <TableCell sx={thSx}>Teléfono</TableCell>
                      <TableCell sx={thSx}>Comuna</TableCell>
                      <TableCell sx={thSx}>Procedimiento</TableCell>
                      <TableCell sx={thSx}>Fecha deseada</TableCell>
                      <TableCell sx={thSx}>Estado</TableCell>
                      <TableCell sx={thSx}>Enlace</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((row) => (
                      <TableRow
                        key={row.request_id}
                        hover
                        onClick={() => navigate(`/admin/cotizaciones/${row.request_id}`)}
                        sx={{ cursor: 'pointer', '&:last-child td': { border: 0 }, '&:hover': { backgroundColor: '#f8fafc' } }}
                      >
                        <TableCell sx={{ pl: 3, py: 1.5, borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
                          {row.created_at
                            ? new Date(row.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{row.nombre} {row.apellido}</div>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#475569' }}>
                          {row.telefono || '—'}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#475569' }}>
                          {row.comuna_name || '—'}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#475569' }}>
                          {PROCEDIMIENTO_LABELS[row.procedimiento] || row.procedimiento}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
                          {row.fecha_deseada ? new Date(row.fecha_deseada).toLocaleDateString('es-CL') : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                          <Chip
                            label={
                              row.status === 'cerrada' ? 'Cerrada'
                              : row.status === 'cotizacion_generada' ? `Cotización generada (${row.offers_count})`
                              : 'Pendiente'
                            }
                            size="small"
                            sx={{
                              fontWeight: 600, fontSize: 11,
                              bgcolor: row.status === 'cerrada' ? '#f1f5f9' : row.status === 'cotizacion_generada' ? '#e8f5ee' : '#fff4e5',
                              color: row.status === 'cerrada' ? '#475569' : row.status === 'cotizacion_generada' ? '#1f7a52' : '#9c6b2e',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9' }} onClick={e => e.stopPropagation()}>
                          <Tooltip title="Copiar link">
                            <IconButton size="small" onClick={() => copyLink(row.request_id)}>
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Abrir en una pestaña nueva">
                            <IconButton size="small" onClick={() => window.open(`/admin/cotizaciones/${row.request_id}`, '_blank')}>
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        </Box>
      </div>
    </div>
  );
}
